import { useEffect, useState } from 'react';
import styled from 'styled-components';
import Button from 'components/ui-components/Button';
import Card from 'components/ui-components/Card';
import { useHTTP } from 'hooks/useRequest';
import responsive from 'utils/responsive';
import {
  gaMFAEnabled,
  gaMobileNumberUpdated,
} from 'data_layer/ga_authentication_events';
import ResendCodeButton from './ResendCodeButton';
import MFACodeInput from './CodeInput';
import MFATooManyRequestsModal from '../too_many_requests_modal';
import { gaMFAError } from '../../../../data_layer/ga_authentication_events';

const MFAChallenge = ({
  phoneNumber,
  username,
  mfaParam,
  isSignedIn,
  secondsUntilNextRequest,
  redirectUrl,
  tooManyRequestsWarning,
  codeLimitReached,
}) => {
  const [code, setCode] = useState(new Array(6).fill(''));
  const [validateErrors, setValidateErrors] = useState(false);
  const [hasBeenSubmitted, setHasBeenSubmitted] = useState(false);
  const [showRequestErrorModal, setShowRequestErrorModal] =
    useState(codeLimitReached);
  const [error, setError] = useState(null);

  const mfaEndpoint = isSignedIn
    ? '/mycef/mobile_number/verify'
    : '/mfa/answer';

  const mfaRequestBody = isSignedIn
    ? {
        verification_code: code.join(''),
        mfa: mfaParam,
      }
    : { mfa_code: code.join('') };

  const mfaRequestConfig = {
    method: 'POST',
    url: hasBeenSubmitted ? mfaEndpoint : null,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(mfaRequestBody),
  };

  const {
    data: mfaRequestResponse,
    error: mfaRequestError,
    loading,
  } = useHTTP(mfaRequestConfig || null);

  const validateVerificationCode = verificationCode => {
    const isValid = verificationCode.indexOf('') === -1;

    setError(
      !isValid
        ? { mfaCodeInput: { message: 'Please enter a valid code' } }
        : null,
    );

    return isValid;
  };

  const handleSubmit = e => {
    e.preventDefault();
    setValidateErrors(true);
    const success = validateVerificationCode(code);

    if (success) {
      setHasBeenSubmitted(true);
    }
  };

  useEffect(() => {
    setHasBeenSubmitted(false);

    const errorData = mfaRequestError?.error?.data;

    if (mfaRequestResponse?.success) {
      if (isSignedIn) {
        if (mfaParam) {
          gaMFAEnabled();
        } else {
          gaMobileNumberUpdated();
        }
      }
      window.location.href = redirectUrl || mfaRequestResponse?.url;
    } else if (errorData !== undefined) {
      const { errors, url: errorRedirect } = errorData;

      gaMFAError(errors);
      setError({ mfaCodeInput: { message: Object.values(errors)[0] } });

      if (errorRedirect !== undefined) window.location.href = errorRedirect;
    }
  }, [mfaRequestResponse, mfaRequestError]);

  useEffect(() => {
    if (validateErrors) {
      validateVerificationCode(code);
    }
  }, [code, validateErrors]);

  return (
    <FormWrapper className="margin-center width-100">
      <div className="mb-32">
        <h1 className="ff-body fw-600 fs-30 l-fs-36 lh-1-2 l-lh-1 tc-main ta-c mb-12 mt-52">
          Two-Factor Authentication
        </h1>
        {isSignedIn && (
          <p className="fs-18 lh-1-25 tc-secondary ta-c m-fb-60">
            Protect your account by setting up two-factor authentication. A
            verification code will be required in addition to your password when
            you sign in.
          </p>
        )}
      </div>
      <form onSubmit={handleSubmit}>
        <Card variant="large" className="mb-32">
          <div className="mb-55">
            <div className="mb-45">
              <h3 className="ff-body fs-21 m-fs-24 lh-1-2 tc-main mt-0">
                Enter the 6-digit code sent to you at {phoneNumber}.
              </h3>
              {isSignedIn && (
                <p className="tc-secondary fs-16 lh-1-3 mb-0">
                  <a
                    className="td-u"
                    href={`/mycef/mobile_number/edit${
                      mfaParam ? '?mfa=true' : ''
                    }`}>
                    Change Number
                  </a>
                </p>
              )}
            </div>
            <div className="mb-10">
              <MFACodeInput
                code={code}
                setCode={setCode}
                id="mfaCodeInput"
                errors={error}
              />
            </div>
            <ResendCodeButton
              timerDuration={secondsUntilNextRequest}
              username={username}
              requiresPassword={!isSignedIn}
              mfaParam={mfaParam}
              showModal={() => setShowRequestErrorModal(true)}
              className="cef-btn cef-btn--small-feature cef-btn--grey">
              Resend code
            </ResendCodeButton>
          </div>
          <div
            className={`actions fl jc-sb sm-fl-col m-fl-row${
              isSignedIn ? '-reverse ai-c' : 'fw-w'
            }`}>
            <SubmitButton
              type="submit"
              className={`cef-btn cef-btn--green cef-btn--feature width-100${
                isSignedIn ? ' m-ml-24' : ''
              }`}>
              {loading ? (
                <span className="cef-btn__loader bs-bb" />
              ) : isSignedIn ? (
                'Continue'
              ) : (
                'Sign in'
              )}
            </SubmitButton>

            {isSignedIn ? (
              <Button
                className="fl ai-c td-u fs-18 tc-grey-60 cef-btn--high cef-btn--as-link"
                handleClick={() => {
                  window.location.href = '/';
                }}>
                Cancel
              </Button>
            ) : (
              <div className="width-100">
                <h4 className="fs-14 lh-1-1 mt-52">Need help?</h4>
                <p>
                  If you can&apos;t receive the code or have changed your mobile
                  number, please call customer services on{' '}
                  <a href="tel:01763 272 717">01763 272 717</a>.
                </p>
              </div>
            )}
          </div>
        </Card>
      </form>
      <MFATooManyRequestsModal
        modalOpen={showRequestErrorModal}
        closeModal={() => setShowRequestErrorModal(false)}
        content={tooManyRequestsWarning}
      />
    </FormWrapper>
  );
};

const FormWrapper = styled.div`
  ${responsive('desktopSmall')} {
    max-width: 650px;
  }
`;

const SubmitButton = styled(Button)`
  ${responsive('desktopSmall')} {
    max-width: 250px;
  }
`;

export default MFAChallenge;
